/**
 * Centralized tRPC Query Cache Configuration
 * 
 * 3-Tier caching strategy for optimal performance and data freshness:
 * - STATIC: Long-lived content that rarely changes (1hr stale, 24hr cache)
 * - DYNAMIC: Discovery/list content with moderate freshness (30s stale, 10min cache) 
 * - REALTIME: Social features requiring instant updates (0 stale, 1min cache)
 */

import type { UseQueryOptions } from "@tanstack/react-query";

// Helper type for cache configuration without queryKey
type CacheConfig = Omit<UseQueryOptions, 'queryKey' | 'queryFn'>;

// Cache tier configurations
export const CACHE_TIERS = {
	/**
	 * Static Content - Long cache for rarely changing data
	 * Use for: Festival details, artist profiles, venue info, filter options
	 */
	STATIC: {
		staleTime: 60 * 60 * 1000, // 1 hour (was 15 minutes)
		gcTime: 24 * 60 * 60 * 1000, // 24 hours (was 1 hour)
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: true,
	},

	/**
	 * Dynamic Content - Short cache for social features and lists
	 * Use for: Facepiles, festival lists, search results, discovery feeds
	 */
	DYNAMIC: {
		staleTime: 30 * 1000, // 30 seconds (was 5 minutes)
		gcTime: 10 * 60 * 1000, // 10 minutes (was 30 minutes)
		refetchOnWindowFocus: true, // Changed to true for freshness
		refetchOnMount: false,
		refetchOnReconnect: true,
	},

	/**
	 * Real-Time Content - No cache for user's own data
	 * Use for: User's own profile, auth state, permissions, own reviews
	 */
	REALTIME: {
		staleTime: 0, // Always fresh
		gcTime: 60 * 1000, // 1 minute (was 30 seconds)
		refetchOnWindowFocus: true,
		refetchOnMount: true,
		refetchOnReconnect: true,
	},
} as const;

// Type for cache tier names
export type CacheTier = keyof typeof CACHE_TIERS;

// Endpoint to cache tier mapping
export const ENDPOINT_CACHE_MAP: Record<string, CacheTier> = {
	// Static content endpoints
	"festival.getBySlug": "STATIC",
	"festival.getFilters": "STATIC",
	"festival.getSets": "STATIC", // Set data + review aggregations (facepiles fetched separately)
	"festival.getSetDetails": "STATIC",
	"festival.getAvailableEventTypes": "STATIC", // Event types available for event group selector
	"artist.getArtistDetails": "STATIC",
	"artist.getStats": "STATIC", // Artist stats rarely change, enable stale-while-revalidate
	"artist.getTotalCount": "STATIC",
	"artist.getAvailableYears": "STATIC",
	"artist.getCounts": "STATIC",
	"artist.getTopTrackPreview": "STATIC",
	"artist.getProfileWithSets": "REALTIME", // Contains user-specific facepile data
	"artist.getGenres": "STATIC",
	"group.getProfile": "STATIC",
	"group.getMetadata": "STATIC",
	"group.getStats": "STATIC",
	"group.getCounts": "STATIC",
	"group.getAvailableYears": "STATIC",
	"show.getShowDetails": "STATIC",
	"show.getBySlug": "STATIC",
	"show.getFilters": "STATIC", // Filter options (locations, months, venues, genres)

	// Dynamic content endpoints
	"festival.list": "DYNAMIC",
	"artist.list": "DYNAMIC",
	"artist.searchArtists": "DYNAMIC",
	"artist.getPopularArtists": "DYNAMIC",
	"artist.getFestivals": "DYNAMIC", // Artist festival history (cached in Phase 2)
	"artist.getSets": "DYNAMIC", // Artist set history (cached in Phase 2)
	"artist.getShows": "DYNAMIC", // Artist show history (cached in Phase 2)
	"show.list": "DYNAMIC",
	"show.getUpcomingShows": "DYNAMIC",
	"search.globalSearch": "DYNAMIC",

	// Community content - can be cached
	"festival.getCommunitySetReviews": "DYNAMIC",
	"festival.getProfileOwnerFestivalSetReviews": "DYNAMIC",
	"show.getCommunityShowReviews": "DYNAMIC",

	// Social interactions - friend attendance/interactions
	"festival.getSocialInteractions": "DYNAMIC", // Friend interactions on festival sets
	"festival.getSocialInteractionsForList": "DYNAMIC", // Friend count per festival (for discovery)
	"show.getSocialInteractionsForList": "DYNAMIC", // Friend count per show (for discovery)
	"festival.getFacepileData": "DYNAMIC", // Facepile avatars for sets
	"show.getFacepileData": "DYNAMIC", // Facepile avatars for shows

	// Public profile endpoints - can be cached when viewing others' profiles
	"user.getProfile": "DYNAMIC",
	"user.getProfileByUsername": "DYNAMIC",
	"user.getProfileContent": "DYNAMIC",
	"user.getUserStats": "DYNAMIC",
	"user.getProfileFestivals": "DYNAMIC",
	"user.getProfileShows": "DYNAMIC",
	"user.getProfileSets": "DYNAMIC",
	"user.getProfileArtists": "DYNAMIC",
	"user.getFestivalReviews": "DYNAMIC",
	"user.getUserAvailableYears": "DYNAMIC",
	"user.getYearInReview": "STATIC", // Historical data, rarely changes

	// Real-time content endpoints (personal/user-specific)
	"festival.getUserFestivalStatus": "REALTIME",
	"festival.getUserSetReview": "REALTIME",
	"festival.getUserFestivalData": "REALTIME",
	"festival.getFestivalAttendees": "REALTIME",
	"show.getUserShowReview": "REALTIME",
	"user.getUserPreferences": "REALTIME",
	"social.getNotifications": "REALTIME",
	"social.getFollowStatus": "REALTIME",
	"social.getFollowStats": "REALTIME",
	// Follower/Following lists moved to DYNAMIC for better caching
	"social.getFollowersList": "DYNAMIC",
	"social.getFollowingList": "DYNAMIC",
	"social.getInvitedUsersList": "DYNAMIC",
	"social.getInviteStats": "DYNAMIC",
	"user.getInviteStats": "DYNAMIC",
	"user.getInviteList": "DYNAMIC",
};

/**
 * Get cache configuration for a tRPC endpoint
 * @param endpoint - The tRPC endpoint path (e.g., "festival.getBySlug")
 * @returns The cache configuration for the endpoint
 */
export function getCacheConfig(endpoint: string): CacheConfig {
	// Check exact match first
	if (endpoint in ENDPOINT_CACHE_MAP) {
		const tier = ENDPOINT_CACHE_MAP[endpoint] as CacheTier;
		return CACHE_TIERS[tier];
	}

	// Check pattern matches
	// All social.* endpoints are real-time
	if (endpoint.startsWith("social.") || endpoint.startsWith("user.")) {
		return CACHE_TIERS.REALTIME;
	}

	// Search endpoints are dynamic
	if (endpoint.includes("search") || endpoint.includes("list")) {
		return CACHE_TIERS.DYNAMIC;
	}

	// Community/user reviews are real-time
	if (
		endpoint.includes("getCommunity") ||
		endpoint.includes("getUser") ||
		endpoint.includes("Review")
	) {
		return CACHE_TIERS.REALTIME;
	}

	// Default to dynamic for unknown endpoints
	return CACHE_TIERS.DYNAMIC;
}

/**
 * Helper to get cache config by tier name
 * @param tier - The cache tier name
 * @returns The cache configuration for the tier
 */
export function getCacheConfigByTier(tier: CacheTier): CacheConfig {
	return CACHE_TIERS[tier];
}